import { initializeApp } from 'firebase/app'
import { getDatabase, ref, onValue } from 'firebase/database'
import { createContext, useContext, useState, useEffect } from 'react'

import { ErrorMaintenance } from '@/components/Error/ErrorMaintenance/ErrorMaintenance'

const firebaseConfig = {
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
}

const app = initializeApp(firebaseConfig)
const database = getDatabase(app)

export type FirebaseContextType = {
  isAppEnabled: boolean
  isLoading: boolean

  eventsAreEnabled: boolean
  flashSalesAreEnabled: boolean
  giveawaysAreEnabled: boolean
  logsAreEnabled: boolean
  shipmentsAreEnabled: boolean
  auctionAreEnabled: boolean
  displayCustomMessage: boolean

  customMessage: {
    fr: string
    en: string
    de: string
  }

  statusTitle: {
    fr: string
    en: string
    de: string
  }
}

const initialContext: FirebaseContextType = {
  isAppEnabled: true,
  isLoading: true,
  eventsAreEnabled: true,
  flashSalesAreEnabled: true,
  giveawaysAreEnabled: true,
  logsAreEnabled: true,
  shipmentsAreEnabled: true,
  auctionAreEnabled: true,
  displayCustomMessage: false,
  customMessage: {
    fr: '',
    en: '',
    de: '',
  },
  statusTitle: {
    fr: '',
    en: '',
    de: '',
  },
}

const FirebaseContext = createContext<FirebaseContextType>(initialContext)

export const useAppStatus = () => useContext(FirebaseContext)

type FirebaseProviderProps = {
  children: React.ReactNode
}

export const FirebaseProvider = ({ children }: FirebaseProviderProps) => {
  const [state, setState] = useState<FirebaseContextType>(initialContext)

  useEffect(() => {
    const unsubscribe = onValue(ref(database, '/'), (snapshot) => {
      try {
        const val = snapshot.val()
        setState({
          isAppEnabled: val?.isAppEnabled,
          isLoading: false,

          auctionAreEnabled: val?.features?.auction,
          eventsAreEnabled: val?.features?.event,
          flashSalesAreEnabled: val?.features?.flashSale,
          giveawaysAreEnabled: val?.features?.giveaway,
          logsAreEnabled: val?.features?.log,
          shipmentsAreEnabled: val?.features?.shipment,

          displayCustomMessage: val?.alert?.isEnabled,
          customMessage: {
            fr: val?.alert?.fr,
            en: val?.alert?.en,
            de: val?.alert?.de,
          },
          statusTitle: {
            fr: val?.alert?.titleFr,
            en: val?.alert?.titleEn,
            de: val?.alert?.titleDe,
          },
        })
      } catch {
        setState({ ...state, isLoading: false })
      }
    })

    return () => unsubscribe()
  }, [])

  if (!state.isAppEnabled) {
    return null
  }

  return (
    <FirebaseContext.Provider value={state}>
      {state.isAppEnabled ? children : <ErrorMaintenance />}
    </FirebaseContext.Provider>
  )
}
